import {Component, inject, Input, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {CmsApiService} from '../../core/cms-api.service';
import {KulturNavFolder} from '../../core/definitions/kultur-nav/kultur-nav-folder';
import {DateToolsService} from '../../core/date-tools.service';
import {Sort} from '@angular/material/sort';
import moment from 'moment';
import {AlertDialogComponent} from '../alert-dialog/alert-dialog.component';
import {ConfirmDialogData} from '../../object-edit/confirm-dialog/confirm-dialog.component';
import {KulturNavParams} from '../../core/definitions/kultur-nav/kultur-nav-params';
import {LoggerService} from "../../core/logger.service";
import {TranslateService} from "@ngx-translate/core";
import {OperationService} from "../../operations/operation.service";
import {OperationTarget} from "../../core/definitions/operation-target.enum";
import {SectionsContainer} from "../../core/definitions/sections-container";
import {SuperObjectModel} from "../../core/definitions/super-object-model";

@Component({
    selector: 'app-kultur-nav-importer-folders',
    templateUrl: './kultur-nav-importer-folders.component.html',
    styleUrls: ['./kultur-nav-importer-folders.component.scss'],
    standalone: false
})
export class KulturNavImporterFoldersComponent implements OnInit {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly dateTools: DateToolsService = inject(DateToolsService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly translate: TranslateService = inject(TranslateService);

  @Input() dialogRef: MatDialogRef<any>;
  @Input() kulturNavData: KulturNavParams;

  displayedColumns = [  // Column order for the table
    'selected',
    'folderName',
    'remoteListName',
    'sourceUpdatedAt',
    'updated',
    'localUpdatedAt',
    'ownerType'
  ];
  kulturNavFolderDataSource: KulturNavFolder[] = [];   // DataSource from the DB
  sortedDataSource: KulturNavFolder[] = [];   // Sorted DataSource
  selectedMap: Map<any, any> = new Map(); // Hold artifact_id of selected folders
  importedStatusMessage: string[] = []; // Holds the status message for import status alert
  search = ''; // used for search filter
  progress: number = null; // Progress for progressbar

  errors = [];
  importing = false;

  ngOnInit(): void {
    this.init().then();

  }

  async init() {
    try {
      this.importing = true;
      this.kulturNavFolderDataSource = await this.cms.retrieveKulturNavFolders({
        concept_type_id: this.kulturNavData.concept_type_id,
        suppressErrHandler: true
      });
    } catch (e) {
      this.errors.push('TRANS__CULTURE_HUB_FOLDERS__RETRIEVING_FOLDERS_FAILED');
      this.errors.push(e.error?.message);
    }
    this.sortedDataSource = this.deepCopy(this.kulturNavFolderDataSource);
    this.importing = false;
  }

  async runImport() {
    this.progress = 1;
    this.importing = true;

    const operationContainer = await this.operationService.createOperationContainer(
      OperationTarget.KULTUR_NAV_FOLDER_IMPORT)
    await this.operationService.setOperations(operationContainer);
    if (!operationContainer.operations.length) {
      await this.legacyImport();
      return;
    }
    this.operationService.setCurrentOperation(operationContainer, operationContainer.operations[0]);
    const sectionContainers: SectionsContainer[] = Array.from(this.selectedMap.values()).map(folder => {
      const sectionsContainer = new SectionsContainer();
      const rootObject: unknown = {
        concept_type_id: this.kulturNavData.concept_type_id,
        artifact_id: folder.artifact_id
      }
      sectionsContainer.rootObject = <SuperObjectModel>rootObject;
      return sectionsContainer;
    });
    this.operationService.setCurrentOperationSectionContainers(operationContainer, sectionContainers);
    await this.operationService.executeOperationStep(operationContainer);

    this.importing = false;
    await this.init();
  }

  // To be removed when all backends are >= 10.0.232
  private async legacyImport() {
    this.progress = 1;
    this.importing = true;

    let index = 1; // Used for progress calculation
    const length = this.selectedMap.size; // get Map Size
    const removeSelected = []; // Holds artifact_id to be removed after loop

    for (const [artifact_id, folder] of this.selectedMap.entries()) {
      try {
        await this.cms.updateKulturNavFolder({
          concept_type_id: this.kulturNavData.concept_type_id,
          artifact_id: artifact_id,
          suppressErrHandler: true
        });
        const okText = this.translate.instant('TRANS__CULTURE_HUB_IMPORTER__FOLDER_IMPORT_OK');
        this.importedStatusMessage.push(`<strong>${folder.name.name}:</strong>&nbsp;${okText}`)
        removeSelected.push(artifact_id);
      } catch (e) {
        if (e.status !== 504) {
          const failedText = this.translate.instant('TRANS__CULTURE_HUB_IMPORTER__FOLDER_IMPORT_FAILED');
          this.importedStatusMessage.push(`<strong>${folder.name.name}:</strong>&nbsp;${failedText}`);
          this.progress = 100;
          break;
        } else {
          const runningText = this.translate.instant('TRANS__CULTURE_HUB_IMPORTER__FOLDER_IMPORT_RUNNING');
          this.logger.warn(`Updating folder ${folder.name.name} caused timeout, but continuing to next folder`);
          this.importedStatusMessage.push(`<strong>${folder.name.name}:</strong>&nbsp;${runningText}`);
        }
      }
      this.progress = (index * 100) / length;
      index++; // increase index
    }

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      panelClass: 'alert-dialog',
      data: {
        modalTitle: 'TRANS__CULTURE_HUB_IMPORTER__IMPORT_STATUS',
        modalContent: this.importedStatusMessage,
      } as ConfirmDialogData
    });

    dialogRef.afterClosed().subscribe(() => {
      this.progress = null;
      this.importedStatusMessage = [];
      removeSelected.forEach(artifact_id => {
        this.selectedMap.delete(artifact_id);
      })
    });

    this.importing = false;
    await this.init();
  }

  closeImport() {
    this.dialogRef.close();
  }

  /**
   * Converts input to Date
   * @param isoDate
   */
  getDateFromIso(isoDate: string) {
    if (isoDate) {
      return this.dateTools.isoDateToString(isoDate);
    } else {
      return '';
    }
  }

  /**
   * Handles changes to selected checkbox
   * @param folder
   */
  selectFolder(folder: KulturNavFolder) {
    if (!this.selectedMap.has(folder.artifact_id)) {
      this.selectedMap.set(folder.artifact_id, folder);
    } else {
      this.selectedMap.delete(folder.artifact_id);
    }
  }

  /**
   * Sort the table
   * @param sort sorting criteria
   */
  sortData(sort: Sort) {
    const data = this.deepCopy(this.kulturNavFolderDataSource);

    if (!sort.active || sort.direction === '') {
      this.sortedDataSource = data;
      return;
    }

    this.sortedDataSource = data.sort((a: any, b: any) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'selected':
          return this.compareSelect(a, b, isAsc);
        case 'folderName':
          return this.compare(a.name.name?.toLowerCase(), b.name.name?.toLowerCase(), isAsc);
        case 'remoteListName':
          return this.compare(a.remote_list.remote_list_name?.toLowerCase(), b.remote_list.remote_list_name?.toLowerCase(), isAsc);
        case 'sourceUpdatedAt':
          return this.compare(a.source_updated_at, b.source_updated_at, isAsc);
        case 'updated':
          return this.compare(!!a.imported_date, !!b.imported_date, isAsc);
        case 'localUpdatedAt':
          return this.compare(a.imported_date, b.imported_date, isAsc);
        case 'ownerType':
          return this.compare(a.folder_type.folder_type_id_value?.toLowerCase(),
            b.folder_type.folder_type_id_value?.toLowerCase(), isAsc);
        default:
          return 0;
      }
    });
  }

  /**
   * Used to compare input a and b
   * @param a
   * @param b
   * @param isAsc
   */
  private compare(a: number | string | boolean, b: number | string | boolean, isAsc: boolean) {
    if (!a || !b) {
      return (!a ? 1 : -1) * (isAsc ? 1 : -1);
    } else {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  }

  /**
   * Used to compare selected
   * @param a
   * @param b
   * @param isAsc
   */
  private compareSelect(a: KulturNavFolder, b: KulturNavFolder, isAsc: boolean
  ) {
    if (!this.selectedMap.has(a.artifact_id) || !this.selectedMap.has(b.artifact_id)) {
      return (this.selectedMap.has(a.artifact_id) ? -1 : 1) * (isAsc ? 1 : -1);
    } else {
      return 0;
    }
  }

  /**
   * Deep Copy
   * @param data input to this method
   */
  private deepCopy(data: any[]) {
    return JSON.parse(JSON.stringify(data));
  }

  /**
   * Check if both dates are not null
   * Then do a compare returning true if updated_at is before source_updated_at
   * @param folder
   */
  isBefore(folder: KulturNavFolder) {
    if (!folder.source_updated_at || !folder.updated_at) {
      return false;
    } else {
      const momentImported = moment(folder.updated_at);
      const momentChanged = moment(folder.source_updated_at);
      return momentImported.isBefore(momentChanged);
    }
  }
}
